import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Plus,
  Search,
  AlertTriangle,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  Bell,
  FileText,
  Timer
} from 'lucide-react';
import { incidentsApi, assetsApi } from '../lib/api';
import { toast } from 'sonner';
import { cn, formatDate, formatDateTime } from '../lib/utils';
import { DocumentsLink } from '../components/DocumentsLink';

const severities = [
  { value: 'critico', label: 'Crítico', color: 'bg-red-50 text-red-700 border-red-200' },
  { value: 'alto', label: 'Alto', color: 'bg-orange-50 text-orange-700 border-orange-200' },
  { value: 'medio', label: 'Médio', color: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
  { value: 'baixo', label: 'Baixo', color: 'bg-green-50 text-green-700 border-green-200' },
];

const statuses = [
  { value: 'aberto', label: 'Aberto', color: 'bg-red-50 text-red-700 border-red-200' },
  { value: 'em_analise', label: 'Em Análise', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  { value: 'em_contencao', label: 'Em Contenção', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  { value: 'em_recuperacao', label: 'Em Recuperação', color: 'bg-purple-50 text-purple-700 border-purple-200' },
  { value: 'resolvido', label: 'Resolvido', color: 'bg-green-50 text-green-700 border-green-200' },
];

const incidentTypes = [
  { value: 'malware', label: 'Malware/Ransomware' },
  { value: 'phishing', label: 'Phishing' },
  { value: 'ddos', label: 'DDoS' },
  { value: 'unauthorized_access', label: 'Acesso Não Autorizado' },
  { value: 'data_breach', label: 'Violação de Dados' },
  { value: 'insider_threat', label: 'Ameaça Interna' },
  { value: 'vulnerability', label: 'Exploração de Vulnerabilidade' },
  { value: 'service_disruption', label: 'Interrupção de Serviço' },
  { value: 'other', label: 'Outro' },
];

const initialFormData = {
  title: '',
  description: '',
  incident_type: 'other',
  severity: 'medio',
  asset_ids: [],
  detected_at: '',
  occurred_at: '',
  affected_systems: '',
  estimated_impact: '',
};

export default function Incidents() {
  const [incidents, setIncidents] = useState([]);
  const [assets, setAssets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingIncident, setEditingIncident] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [isSaving, setIsSaving] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);

  useEffect(() => {
    loadData();
  }, [severityFilter, statusFilter]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (severityFilter) params.severity = severityFilter;
      if (statusFilter) params.status = statusFilter;
      
      const [incidentsRes, assetsRes] = await Promise.all([
        incidentsApi.getAll(params),
        assetsApi.getAll({}),
      ]);
      
      setIncidents(incidentsRes.data);
      setAssets(assetsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('O título é obrigatório');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        ...formData,
        affected_systems: formData.affected_systems ? formData.affected_systems.split(',').map(s => s.trim()) : [],
      };

      if (editingIncident) {
        await incidentsApi.update(editingIncident.incident_id, payload);
        toast.success('Incidente atualizado com sucesso');
      } else {
        await incidentsApi.create(payload);
        toast.success('Incidente registado com sucesso');
      }
      
      setShowForm(false);
      setEditingIncident(null);
      setFormData(initialFormData);
      loadData();
    } catch (error) {
      toast.error('Erro ao guardar incidente');
    } finally {
      setIsSaving(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedIncident) return;
    try {
      await incidentsApi.update(selectedIncident.incident_id, { status: newStatus });
      toast.success('Estado atualizado com sucesso');
      setShowStatusDialog(false);
      loadData();
    } catch (error) {
      toast.error('Erro ao atualizar estado');
    }
  };

  const handleEdit = (incident) => {
    setEditingIncident(incident);
    setFormData({
      title: incident.title,
      description: incident.description || '',
      incident_type: incident.incident_type,
      severity: incident.severity,
      asset_ids: incident.asset_ids || [],
      detected_at: incident.detected_at || '',
      occurred_at: incident.occurred_at || '',
      affected_systems: incident.affected_systems?.join(', ') || '',
      estimated_impact: incident.estimated_impact || '',
    });
    setShowForm(true);
  };

  const filteredIncidents = incidents.filter(
    (inc) =>
      inc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inc.incident_id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const stats = incidents.reduce(
    (acc, inc) => {
      acc.total++;
      if (inc.status === 'aberto') acc.open++;
      if (inc.severity === 'critico') acc.critical++;
      if (inc.status === 'resolvido') acc.resolved++;
      return acc;
    },
    { total: 0, open: 0, critical: 0, resolved: 0 }
  );

  const getSeverityConfig = (sev) => severities.find(s => s.value === sev) || severities[2];
  const getStatusConfig = (st) => statuses.find(s => s.value === st) || statuses[0];
  const getTypeLabel = (t) => incidentTypes.find(it => it.value === t)?.label || t;

  const headerContent = (
    <Button onClick={() => { setEditingIncident(null); setFormData(initialFormData); setShowForm(true); }} data-testid="new-incident-btn">
      <Plus className="h-4 w-4 mr-2" />
      Registar Incidente
    </Button>
  );

  if (isLoading) {
    return (
      <Layout title="Gestão de Incidentes">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Gestão de Incidentes" headerContent={headerContent}>
      <div className="space-y-6" data-testid="incidents-container">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="card-flat">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <AlertTriangle className="h-8 w-8 text-slate-400" />
                <span className="text-2xl font-bold text-slate-900">{stats.total}</span>
              </div>
              <p className="text-sm text-slate-500 mt-2">Total de Incidentes</p>
            </CardContent>
          </Card>
          <Card className="card-flat border-l-4 border-l-red-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Clock className="h-8 w-8 text-red-500" />
                <span className="text-2xl font-bold text-red-600">{stats.open}</span>
              </div>
              <p className="text-sm text-slate-500 mt-2">Em Aberto</p>
            </CardContent>
          </Card>
          <Card className="card-flat border-l-4 border-l-orange-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Bell className="h-8 w-8 text-orange-500" />
                <span className="text-2xl font-bold text-orange-600">{stats.critical}</span>
              </div>
              <p className="text-sm text-slate-500 mt-2">Críticos</p>
            </CardContent>
          </Card>
          <Card className="card-flat border-l-4 border-l-green-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <CheckCircle className="h-8 w-8 text-green-500" />
                <span className="text-2xl font-bold text-green-600">{stats.resolved}</span>
              </div>
              <p className="text-sm text-slate-500 mt-2">Resolvidos</p>
            </CardContent>
          </Card>
        </div>

        {/* NIS2 Timer Alert */}
        {stats.open > 0 && (
          <Card className="card-flat bg-amber-50 border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Timer className="h-5 w-5 text-amber-600" />
                <div>
                  <p className="font-medium text-amber-800">Prazos NIS2</p>
                  <p className="text-sm text-amber-700">
                    Lembre-se: Incidentes significativos devem ser notificados à autoridade competente em <strong>24 horas</strong> (alerta inicial) e <strong>72 horas</strong> (relatório completo).
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card className="card-flat">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Pesquisar incidentes..."
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="search-incidents-input"
                />
              </div>
              <Select value={severityFilter} onValueChange={(v) => setSeverityFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Severidade" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {severities.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {statuses.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Incidents List */}
        {filteredIncidents.length === 0 ? (
          <Card className="card-flat">
            <CardContent className="p-8 text-center">
              <AlertTriangle className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700">Nenhum incidente encontrado</h3>
              <p className="text-sm text-slate-500 mb-4">
                {searchQuery || severityFilter || statusFilter ? 'Tente ajustar os filtros' : 'Não há incidentes registados'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredIncidents.map((incident) => {
              const sevConfig = getSeverityConfig(incident.severity);
              const statusConfig = getStatusConfig(incident.status);
              
              return (
                <Card key={incident.incident_id} className="card-flat hover:shadow-sm transition-shadow" data-testid={`incident-${incident.incident_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className={cn('h-12 w-12 rounded-lg flex items-center justify-center', sevConfig.color)}>
                        <AlertTriangle className="h-6 w-6" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-mono text-xs text-slate-400">{incident.incident_id}</span>
                              <Badge variant="outline" className={cn('text-xs', sevConfig.color)}>
                                {sevConfig.label}
                              </Badge>
                              <Badge variant="outline" className={cn('text-xs', statusConfig.color)}>
                                {statusConfig.label}
                              </Badge>
                            </div>
                            <h3 className="font-medium text-slate-900">{incident.title}</h3>
                            <p className="text-sm text-slate-500 mt-1">{getTypeLabel(incident.incident_type)}</p>
                          </div>
                          <div className="flex gap-2">
                            <DocumentsLink module="incident" entityId={incident.incident_id} title={incident.title} />
                            <Button variant="outline" size="sm" onClick={() => { setSelectedIncident(incident); setShowStatusDialog(true); }}>
                              Alterar Estado
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleEdit(incident)}>
                              Editar
                            </Button>
                          </div>
                        </div>
                        {incident.description && (
                          <p className="text-sm text-slate-600 mt-2 line-clamp-2">{incident.description}</p>
                        )}
                        <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                          {incident.detected_at && (
                            <span>Detetado: {formatDateTime(incident.detected_at)}</span>
                          )}
                          <span>Criado: {formatDate(incident.created_at)}</span>
                          {incident.asset_ids?.length > 0 && (
                            <span>{incident.asset_ids.length} ativo(s) afetado(s)</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Status Change Dialog */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Alterar Estado do Incidente</DialogTitle>
            <DialogDescription>
              Selecione o novo estado para o incidente {selectedIncident?.incident_id}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-4">
            {statuses.map((status) => (
              <Button
                key={status.value}
                variant={selectedIncident?.status === status.value ? 'default' : 'outline'}
                className="justify-start"
                onClick={() => handleStatusChange(status.value)}
              >
                {status.label}
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Incident Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingIncident ? 'Editar Incidente' : 'Registar Incidente'}</DialogTitle>
            <DialogDescription>
              {editingIncident ? 'Atualize os detalhes do incidente' : 'Registe um novo incidente de cibersegurança'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Título *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Ex: Ataque de ransomware no servidor X"
                required
                data-testid="incident-title-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="incident_type">Tipo de Incidente</Label>
                <Select value={formData.incident_type} onValueChange={(v) => setFormData({ ...formData, incident_type: v })}>
                  <SelectTrigger id="incident_type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {incidentTypes.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="severity">Severidade</Label>
                <Select value={formData.severity} onValueChange={(v) => setFormData({ ...formData, severity: v })}>
                  <SelectTrigger id="severity">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {severities.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Descrição</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descrição detalhada do incidente..."
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="detected_at">Data/Hora Deteção</Label>
                <Input
                  id="detected_at"
                  type="datetime-local"
                  value={formData.detected_at}
                  onChange={(e) => setFormData({ ...formData, detected_at: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="occurred_at">Data/Hora Ocorrência</Label>
                <Input
                  id="occurred_at"
                  type="datetime-local"
                  value={formData.occurred_at}
                  onChange={(e) => setFormData({ ...formData, occurred_at: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="affected_systems">Sistemas Afetados (separados por vírgula)</Label>
              <Input
                id="affected_systems"
                value={formData.affected_systems}
                onChange={(e) => setFormData({ ...formData, affected_systems: e.target.value })}
                placeholder="Ex: Email, ERP, VPN"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="estimated_impact">Impacto Estimado</Label>
              <Textarea
                id="estimated_impact"
                value={formData.estimated_impact}
                onChange={(e) => setFormData({ ...formData, estimated_impact: e.target.value })}
                placeholder="Descreva o impacto potencial do incidente..."
                rows={2}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
              <Button type="submit" disabled={isSaving} data-testid="save-incident-btn">
                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {editingIncident ? 'Guardar' : 'Registar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
